import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import PercentChanged from "../percentChanged/percentChanged";
import MoneyDisplay from "../moneyDisplay/moneyDisplay";
import AnimatedNumber from "react-animated-number";
import AnimatedDonut from "react-animated-donut";
import ReactTooltip from "react-tooltip";
import striptags from "striptags";
import { asNumberString } from "../../utils/formatters";
import { isFinite } from "lodash";
import "./donut.css";
export function getColor(percent) {
  let p = percent / 100;
  let luma = 58;
  let increase = 1.42 + p - 0.82;

  return "hsl(213, 80%, " + luma / increase + "%)";
}

function DonutChart(props) {
  props = {
    value: 16,
    size: 116,
    strokewidth: 21,
    customTitleStyle: "",
    attended: 0,
    totalAttended: 0,
    currencySymbol: "",
    centerToolTip: "",
    growth: 0.23,
    showForecast: false,
    hidePercentChanged: false,
    url: "",
    ...props
  };

  const donutTitle = striptags(props.type);
  const donutTitleRef = useRef();

  useEffect(() => {
    let el = donutTitleRef.current;
    const hasOverflow = el.scrollHeight > el.clientHeight;

    if (hasOverflow) {
      el.setAttribute("data-tip", donutTitle);
    }
  }, []);

  let formatForecastData = () => {
    if (props.forecastValue < props.value) {
      return [
        { value: props.forecastValue, color: "#fd6a3e" },
        { value: props.value - props.forecastValue, color: strokecolor },
        { value: 100 - props.value, color: "transparent" }
      ];
    }

    return [
      { value: props.value, color: strokecolor },
      { value: props.forecastValue - props.value, color: "#fd6a3e" },
      { value: 100 - props.forecastValue, color: "transparent" }
    ];
  };

  const halfsize = props.size * 0.5;
  const radius = halfsize - props.strokewidth * 0.5;
  const strokecolor = getColor(props.value);
  const haveDollarValue = isFinite(props.dollarValue);
  const hasMinusButton = props.hasMinusButton;
  const attended = props.attended > 0;
  let moneyContainer = null;
  if (haveDollarValue) {
    moneyContainer = (
      <MoneyDisplay
        value={props.dollarValue}
        size={props.fontSize}
        currency={props.currencySymbol}
        url={props.url}
      />
    );
  }

  // const indicatorstyle = {strokeWidth: props.strokewidth, strokeDasharray: dashval, stroke: strokecolor}
  const rotateval = "rotate(-90 " + halfsize + "," + halfsize + ")";
  const forecastData = formatForecastData();
  const circleStyle = {
    ...styles.donutchartTrack,
    strokeWidth: props.strokewidth
  };
  return (
    <div style={styles.wrapper}>
      {hasMinusButton && (
        <button
          onClick={props.onMinusButtonClick}
          className="minus-button"
          style={styles.minusButton}
          data-testid="minus-button"
        >
          &ndash;
        </button>
      )}
      <div style={{ minHeight: props.size, minWidth: props.size }}>
        <svg
          width={props.size}
          height={props.size}
          style={styles.donutchart}
          data-testid="donut-chart"
        >
          <circle
            r={radius}
            cx={halfsize}
            cy={halfsize}
            transform={rotateval}
            style={circleStyle}
            className="donutchart-track"
          />
          {/*TODO: fix error in AnimatedDonut*/}
          {props.showForecast ? (
            <AnimatedDonut data={forecastData} speed={(5 * props.value) / 100} width={26} />
          ) : (
            <AnimatedDonut
              data={[
                { value: props.value, color: strokecolor },
                { value: 100 - props.value, color: "transparent" }
              ]}
              speed={(5 * props.value) / 100}
              width={26}
            />
          )}
          <text
            data-tip={props.centerToolTip}
            className="donutchart-text"
            x={halfsize}
            y={halfsize + 7}
            style={styles.donutchartText}
          >
            <AnimatedNumber
              component="tspan"
              value={props.value}
              style={{
                margin: 0,
                padding: 0,
                display: "inline",
                fontWeight: "100",
                transition: "0.8s ease-out",
                fontSize: "24px"
              }}
              className="donutchart-text-val"
              duration={1000}
              stepPrecision={0}
              formatValue={(n) => n.toFixed(0)}
            />

            <tspan className="donutchart-text-percent" style={styles.donutchartTextPercent}>
              %
            </tspan>
          </text>
        </svg>
      </div>
      <div style={styles.title} className={"donut-title " + props.customTitleStyle}>
        <p data-tip="" ref={donutTitleRef} className="donutTooltip">
          {donutTitle}
        </p>
      </div>
      {attended && (
        <div className="attended">
          <div>
            <p className="totals" style={styles.attended} data-testid="attended-totals">
              {asNumberString(props.attended)} of {asNumberString(props.totalAttended)}
            </p>
            <p className="attended-sub" style={styles.attendedSub}>
              attended
            </p>
          </div>
        </div>
      )}
      <div
        style={styles.moneyDisplayWrapper}
        className="donut-title"
        data-testid="money-display-wrapper"
      >
        {moneyContainer}
      </div>
      {props.average == null ? null : (
          <div style={styles.average}>
            <div style={styles.averageText}>{props.averageText}</div>
            <div style={styles.averageValue}>{props.average}</div>
          </div>
      )}
      {props.hidePercentChanged ? null : (
        <div
          style={styles.percentChangedWrapper}
          className="donut-title"
          data-testid="percent-changed-wrapper"
        >
          <PercentChanged changeValue={props.growth} toolTip={props.toolTip} showBorder />
        </div>
      )}
      <ReactTooltip />
    </div>
  );
}

let styles = {
  wrapper: {
    display: "inline-flex",
    flexDirection: "column",
    alignItems: "center"
  },
  cta: {
    textDecoration: "none"
  },
  donutchartTrack: {
    fill: "transparent",
    stroke: "rgba(93, 101, 118, 0.2)",
    strokeWidth: "21"
  },
  donutchartIndicator: {
    fill: "transparent",
    stroke: "#ff664e",
    strokeWidth: "21",
    strokeDasharray: "0 10000",
    transition: "stroke-dasharray .3s ease"
  },
  donutchart: {
    borderRadius: "50%",
    display: "block"
    // border: 'solid 1px #5a5f69'
  },
  donutchartText: {
    fill: "#d9eaff",
    textAnchor: "middle",
    top: "50px",
    position: "relative",
    fontWeight: "400"
  },
  donutchartTextVal: {
    fontSize: "24px"
  },
  donutchartTextPercent: {
    fontSize: "24px",
    fontWeight: 100
  },
  title: {
    textAlign: "center",
    color: "#8791a0",
    paddingTop: "15px",
    lineHeight: "17px"
  },
  attended: {
    textAlign: "center",
    margin: "0",
    padding: "8px 0 0 0 ",
    color: "#92bef4"
  },
  attendedSub: {
    color: "#8791a0",
    textAlign: "center",
    margin: "-8px 0 0 0",
    padding: "0 0 5px 0",
    fontSize: "12px"
  },
  percentChangedWrapper: {
    textAlign: "center",
    left: "-9px",
    position: "relative",
    top: "15px"
  },
  moneyDisplayWrapper: {
    textAlign: "center"
  },
  moneyDisplay: {
    marginBottom: "10px",
    display: "inline-block"
  },
  minusButton: {
    alignSelf: "end",
    border: "solid 1px #8bb73c",
    minWidth: "10px",
    height: "18px",
    borderRadius: "10px",
    position: "relative",
    left: "8px",
    top: "15px",
    cursor: "pointer",
    color: "#8bb73c",
    lineHeight: "0px",
    padding: "5px",
    background: "transparent",
    fontSize: "12px"
  },
 
  average: {
    display: "inline-block",
    position: "relative",
    marginTop: "10px",
    textAlign: "center",
    color: "rgb(74, 155, 255)",
  },
  averageText: {
    position: "absolute",
    height: "25px",
    whiteSpace: "nowrap",
    color: "rgb(135, 145, 160)"
  },
  averageValue: {
    position: "relative",
    top: "25px",
    fontWeight: "100"
  }
};

export default DonutChart;

DonutChart.propTypes = {
  value: PropTypes.any,
  size: PropTypes.number,
  strokewidth: PropTypes.number,
  type: PropTypes.string,
  growth: PropTypes.number,
  dollarValue: PropTypes.number,
  fontSize: PropTypes.number,
  onMinusButtonClick: PropTypes.func,
  hasMinusButton: PropTypes.bool,
  customTitleStyle: PropTypes.string,
  attended: PropTypes.number,
  totalAttended: PropTypes.number,
  currencySymbol: PropTypes.string,
  toolTip: PropTypes.string,
  centerToolTip: PropTypes.string,
  forecastValue: PropTypes.number,
  showForecast: PropTypes.bool,
  hidePercentChanged: PropTypes.bool,
  url: PropTypes.string,
  average: PropTypes.number,
  averageText: PropTypes.string
};

DonutChart.defaultProps = {
  fontSize: 15
};
